import dayjs from "dayjs";
import { get } from "lodash";
import moment from "moment";
import {
  CONTACT_POINT_SYSTEM,
  MAX_FEES_VALUE,
  REGEX_EXPS,
  SALE_MODES,
} from "utils/constants";
import { isMajor } from "utils/helpers/date";
import {
  childHasSocialRegimeAttachmentMismatch,
  isChildLinkedToAdherent,
} from "utils/helpers/miltisRelatedPersonChild";
import {
  createContractNumberValidation,
  normalizeTextField,
  stripAllSpaces,
} from "utils/helpers/validations";
import {
  getPhonePrefixFromYupContext,
  isValidLocalPhoneNumber,
} from "utils/helpers/phone";
import { validateBIC, validateIBAN, validateNIR } from "utils/helpers/values";
import * as yup from "yup";

const validation = (messages, { hasPartner, checkUser, isLumineis }) => {
  const string = yup.string().typeError(messages.NOT_STRING).nullable();
  const textField = yup
    .string()
    .transform(normalizeTextField)
    .typeError(messages.NOT_STRING)
    .nullable();
  const noSpaceString = yup.string().transform(stripAllSpaces);
  const optionalDate = () =>
    yup
      .date()
      .transform((value, originalValue) =>
        originalValue === "" || originalValue == null ? null : value,
      )
      .nullable()
      .typeError(messages.INVALID_VALUE);
  const nameValidation = textField
    .notRequired()
    .test(
      "is-valid-name",
      messages.INVALID_NAME,
      (value) => !value || REGEX_EXPS.name.test(value),
    );

  return yup.object().shape({
    createdDate: yup.date(),
    isPayerReceiver: yup.string().typeError(messages.NOT_STRING),
    additionalInfo: yup
      .object()
      .shape({
        brokerageFees: yup
          .number()
          .max(MAX_FEES_VALUE, `${messages.MAX} ${MAX_FEES_VALUE}`)
          .min(0, `${messages.MIN} 0`)
          .nullable()
          .optional(),
      })
      .optional(),
    project: yup.object().shape({
      projectType: yup.mixed().when("contract.saleMode", {
        is: (saleMode) => saleMode === SALE_MODES.DISTANT,
        then: string,
      }),
      contract: yup.object().shape({
        issuanceDate: yup.date(),
        caseType: string,
        saleMode: string,
      }),
      fields: yup.object().shape({
        selfOccupation: yup.boolean(),
        contractNumber: string,
        dueDate: yup.date(),
        recipient: string,
        reason: string,
        acceptProductStatuses: yup.boolean(),
        acceptAgreement: yup.boolean(),
        acceptFees: yup.boolean(),
      }),
      terminationRequest: yup
        .mixed()
        .when(
          ["fields.selfOccupation", "ria", "fields.contractMoreThanOneYear"],
          {
            is: (selfOccupation, ria, contractMoreThanOneYear) =>
              ria === true &&
              contractMoreThanOneYear === true &&
              selfOccupation === false,
            then: () =>
              yup.object().shape(
                isLumineis
                  ? {
                      contractNumber: createContractNumberValidation(
                        messages,
                        REGEX_EXPS,
                      ),
                      address: textField,
                      zipCode: textField.test(
                        "zipCode",
                        messages.INVALID_VALUE,
                        (value) => !value || REGEX_EXPS.zipCode.test(value),
                      ),
                      city: textField,
                      email: textField.test(
                        "email",
                        messages.EMAIL,
                        (value) =>
                          !value || yup.string().email().isValidSync(value),
                      ),
                      brokerageMandate: yup.object().shape({
                        currentCompany: textField,
                      }),
                      reason: string,
                    }
                  : {
                      contractNumber: createContractNumberValidation(
                        messages,
                        REGEX_EXPS,
                      ),
                      dueDate: optionalDate().test(
                        "dueDateFormat",
                        messages.INVALID_DUE_DATE,
                        (value) =>
                          !value ||
                          (dayjs(value).isValid() &&
                            dayjs(value).isBefore(dayjs().subtract(1, "year"))),
                      ),
                      recipient: textField,
                      reason: string,
                    },
              ),
            otherwise: () => yup.object().nullable().optional(),
          },
        ),
      contact: yup.object().shape({
        user: yup.object().shape({
          gender: string,
          lastname: nameValidation,
          firstname: nameValidation,
          birthName: nameValidation,
          birthDate: yup.date(),
        }),
        socialRegime: string.test(
          "adherent-regime-children",
          messages.SOCIAL_REGIME_ATTACHMENT_MISMATCH,
          function (value) {
            const root = this.from[this.from.length - 1].value;
            const children = get(root, "children", []);
            const hasSpouse = Boolean(get(root, "spouse"));
            const hasMismatch = children.some(
              (child) =>
                isChildLinkedToAdherent({
                  authority: get(child, "authority"),
                  nirInheritanceFrom: get(
                    child,
                    "identity.fields.nirInheritanceFrom",
                  ),
                }) &&
                childHasSocialRegimeAttachmentMismatch({
                  child,
                  adherentSocialRegime: value,
                  spouseSocialRegime: get(root, "spouse.identity.socialRegime"),
                  hasSpouse,
                }),
            );
            if (!hasMismatch) return true;
            return this.createError({
              message: messages.SOCIAL_REGIME_ATTACHMENT_MISMATCH,
            });
          },
        ),
        socialSecurityNumber: noSpaceString
          .typeError(messages.REQUIRED)
          .nullable()
          .optional()
          .test(
            "securityNumberFormat",
            messages.INVALID_VALUE,
            function (value, ctx) {
              if (!value) return true;
              const result = validateNIR(value, this.parent);
              if (!result.valid)
                return ctx.createError({
                  message: messages[result.reason] || messages.INVALID_VALUE,
                });

              return true;
            },
          ),
        socioProfessionalCategory: string,
        politicallyExposedPerson: yup.boolean().nullable(),
        // exerciseFrame: yup.mixed().when("socioProfessionalCategory", {
        //   is: showExerciseFrame,
        //   then: string,
        // }),
        siretNumber: string,
        profession: string,
        acceptContact: yup.boolean().typeError(messages.NOT_BOOLEAN).nullable(),
        acceptDigital: yup.boolean().typeError(messages.NOT_BOOLEAN).nullable(),
        familySituation: string,
        fields: yup.object().shape({
          affiliateOrganizationCode: yup
            .string()
            .typeError(messages.NOT_STRING)
            .nullable(),
        }),
      }),
    }),
    telecoms: yup.object().shape({
      contact: yup.object().shape({
        [CONTACT_POINT_SYSTEM.ADDRESS]: yup.object().shape({
          value: textField,
        }),
        [CONTACT_POINT_SYSTEM.COMPLEMENT]: yup.object().shape({
          value: textField,
        }),
        [CONTACT_POINT_SYSTEM.ZIP_CODE]: yup.object().shape({
          value: string,
        }),
        [CONTACT_POINT_SYSTEM.CITY]: yup.object().shape({
          value: string,
        }),
        [CONTACT_POINT_SYSTEM.DEPARTMENT_OF_BIRTH]: yup.object().shape({
          value: string,
        }),
        [CONTACT_POINT_SYSTEM.BIRTH_COUNTRY]: yup.object().shape({
          value: string,
        }),
        [CONTACT_POINT_SYSTEM.BIRTH_CITY]: yup.object().shape({
          value: string.test(
            "lumineisBirthCity",
            messages.REQUIRED,
            function (value) {
              if (!isLumineis) return true;
              const birthCountry = get(
                this.from?.[this.from.length - 1]?.value,
                `telecoms.contact.${CONTACT_POINT_SYSTEM.BIRTH_COUNTRY}.value`,
              );
              if (birthCountry !== "France") return true;
              return Boolean(String(value || "").trim());
            },
          ),
        }),
        [CONTACT_POINT_SYSTEM.BIRTH_ZIP_CODE]: yup.object().shape({
          value: string.test(
            "lumineisBirthZipCode",
            messages.INVALID_VALUE,
            function (value) {
              if (!isLumineis) return true;
              const birthCountry = get(
                this.from?.[this.from.length - 1]?.value,
                `telecoms.contact.${CONTACT_POINT_SYSTEM.BIRTH_COUNTRY}.value`,
              );
              if (birthCountry !== "France") return true;
              return REGEX_EXPS.zipCode.test(String(value || "").trim());
            },
          ),
        }),
        [CONTACT_POINT_SYSTEM.PHONE]: yup.object().shape({
          value: noSpaceString
            .typeError(messages.REQUIRED)
            .nullable()
            .test("isValid", messages.INVALID_VALUE, function (value) {
              if (!value) {
                return true;
              }
              const prefix = getPhonePrefixFromYupContext(this);
              return isValidLocalPhoneNumber(value, prefix);
            }),
          // .test(
          //   "phoneConflict",
          //   messages.PHONE_CONFLICT,
          //   async function (value) {
          //     const isValidRegex = new RegExp(REGEX_EXPS.phone).test(value);
          //     if (!value || !isValidRegex) {
          //       return true;
          //     }
          // const {
          //   data: { user },
          // } = await checkUser({
          //   variables: {
          //     where: { phone: value },
          //   },
          // });
          // return !user?.id;
          // }
          // ),
        }),
        [CONTACT_POINT_SYSTEM.LANDLINE]: yup.object().shape({
          value: noSpaceString
            .typeError(messages.REQUIRED)
            .nullable()
            .test("isValid", messages.INVALID_VALUE, async function (value) {
              return value ? new RegExp(REGEX_EXPS.phone).test(value) : true;
            }),
        }),
        [CONTACT_POINT_SYSTEM.EMAIL]: yup.object().shape({
          value: yup.string().nullable().email(messages.EMAIL),
          // .test(
          //   "emailConflict",
          //   messages.EMAIL_CONFLICT,
          //   async function (value) {
          //     if (!value || !yup.string().email().isValidSync(value)) {
          //       return true;
          //     }
          //     const {
          //       data: { user },
          //     } = await checkUser({
          //       variables: {
          //         where: { email: value },
          //       },
          //     });
          //     return !user?.id;
          //   }
          // ),
        }),
      }),
      payer: yup.object().shape({
        [CONTACT_POINT_SYSTEM.ADDRESS]: yup.object().shape({
          value: textField,
        }),
        [CONTACT_POINT_SYSTEM.ZIP_CODE]: yup.object().shape({
          value: string,
        }),
        [CONTACT_POINT_SYSTEM.CITY]: yup.object().shape({
          value: string,
        }),
      }),
      receiver: yup.object().shape({
        // [CONTACT_POINT_SYSTEM.ADDRESS]: yup.object().shape({
        //   value: string.required(messages.REQUIRED),
        // }),
        // [CONTACT_POINT_SYSTEM.COMPLEMENT]: yup.object().shape({
        //   value: string,
        // }),
        // [CONTACT_POINT_SYSTEM.ZIP_CODE]: yup.object().shape({
        //   value: string.required(messages.REQUIRED),
        // }),
        // [CONTACT_POINT_SYSTEM.CITY]: yup.object().shape({
        //   value: string.required(messages.REQUIRED),
        // }),
      }),
    }),
    spouse:
      hasPartner &&
      yup.object().shape({
        identity: yup.object().shape({
          user: yup.object().shape({
            lastname: nameValidation,
            firstname: nameValidation,
            birthDate: optionalDate()
              .max(isMajor, messages.RADIANCE.SPOUSE_AGE)
              .min(
                moment().subtract(100, "year").startOf("year"),
                messages.RADIANCE.SPOUSE_AGE,
              ),
          }),
          socioProfessionalCategory: string,
        }),
      }),
    children: yup.array().of(
      yup.object().shape({
        identity: yup.object().shape({
          user: yup.object().shape({
            birthDate: optionalDate().max(moment(), messages.MIN_TODAY),
          }),
        }),
      }),
    ),
    beneficiary: yup.object().shape({
      relationship: string,
      identity: yup.object().shape({
        fields: yup.object().shape({
          designation: string.max(300),
        }),
      }),
    }),
    payment: yup.object().shape({
      type: string,
      frequency: string,
      payer: yup.object().shape({
        BIC: noSpaceString
          .typeError(messages.NOT_STRING)
          .nullable()
          .test(
            "validateBIC",
            messages.INVALID_BIC || messages.INVALID_IBAN,
            function (value) {
              if (!value) return true;
              const result = validateBIC(value);
              return result.valid;
            },
          ),
        IBAN: noSpaceString
          .typeError(messages.NOT_STRING)
          .nullable()
          .test("validateIBAN", messages.INVALID_IBAN, function (value) {
            if (!value) return true;
            const result = validateIBAN(value);
            return result.valid;
          }),
        owner: yup.object().shape({
          isAdherent: yup.boolean().typeError(messages.NOT_BOOLEAN),
          firstName: nameValidation,
          lastName: nameValidation,
        }),
      }),
      receiver: yup.object().shape({
        BIC: noSpaceString
          .typeError(messages.NOT_STRING)
          .nullable()
          .test(
            "validateBIC",
            messages.INVALID_BIC || messages.INVALID_IBAN,
            function (value) {
              if (!value) return true;
              const result = validateBIC(value);
              return result.valid;
            },
          ),
        IBAN: noSpaceString
          .typeError(messages.NOT_STRING)
          .nullable()
          .test("validateIBAN", messages.INVALID_IBAN, function (value) {
            if (!value) return true;
            const result = validateIBAN(value);
            return result.valid;
          }),
        owner: yup.object().shape({
          firstName: nameValidation,
          lastName: nameValidation,
        }),
      }),
    }),
  });
};

export default validation;
